import {
	ChangeDetectionStrategy,
	Component,
	Signal,
	TemplateRef,
	ViewChild,
	WritableSignal,
	computed,
	signal
} from '@angular/core';
import { ExerciseDb } from 'lib/cosmos/exercise/exerciseDb.js';
import { SessionDb } from 'lib/cosmos/session/sessionDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { ListItem } from 'lib/ui/list/index.js';
import { ISelectionCardItem } from 'lib/ui/selection-cards/index.js';
import { ITable, TableComponent } from 'lib/ui/table/index.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { sessionsHelper } from './sessions.helper.js';

@Component({
	templateUrl: './manage-user-sessions.component.html',
	selector: 'mod-manage-user-sessions',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ManagageUserSessionsComponent extends TypeCoreComponent<UserDb.IUserItem> {
	@ViewChild('newSessionTemplate', { static: true }) newSessionTemplate!: TemplateRef<any>;
	@ViewChild('selectWorkoutTemplate', { static: true }) selectWorkoutTemplate!: TemplateRef<any>;
	@ViewChild('editSessionTemplate', { static: true }) editSessionTemplate!: TemplateRef<any>;
	@ViewChild('tableComponent', { static: false }) tableComponent?: TableComponent<SessionDb.ISessionItem>;

	private readonly sessionItems: WritableSignal<SessionDb.ISessionItem[] | undefined> = signal(undefined);
	public readonly table: Signal<ITable<SessionDb.ISessionItem> | undefined> = computed(() => {
		const sessionItems = this.sessionItems();

		if (!sessionItems) {
			return undefined;
		}

		return this.getTable(sessionItems);
	});

	public readonly availableExercises: WritableSignal<ExerciseDb.IExerciseItem[] | undefined> = signal(undefined);
	public readonly selectedSessionItem: WritableSignal<SessionDb.ISessionItem | undefined> = signal(undefined);

	public readonly sessionSelectionItems: Signal<ISelectionCardItem[]> = computed<ISelectionCardItem[]>(() => {
		const appSetup = this.appSetup();

		if (!appSetup) {
			return [];
		}

		const authUser = this.authUser();
		const cardItems: ISelectionCardItem[] = [
			{
				title: sessionsHelper.localizeSessionType('workout'),
				image: appSetup.sessionCardImages.workout?.[0]?.image,
				click: () => {
					this.dependencies.dialogService.closeAllDialogs();
					this.openSelectWorkoutDialog();
				}
			}
		];

		if (authUser?.userType === 'trainer') {
			cardItems.push(
				{
					title: sessionsHelper.localizeSessionType('onlineConsultation'),
					image: appSetup.sessionCardImages.onlineConsultation?.[0]?.image,
					click: () => {
						this.createSession({
							sessionType: 'onlineConsultation',
							stateItems: [],
							workout: undefined
						});
						this.dependencies.dialogService.closeAllDialogs();
					}
				},
				{
					title: sessionsHelper.localizeSessionType('offlineConsultation'),
					image: appSetup.sessionCardImages.offlineConsultation?.[0]?.image,
					click: () => {
						this.createSession({
							sessionType: 'offlineConsultation',
							stateItems: [],
							workout: undefined
						});
						this.dependencies.dialogService.closeAllDialogs();
					}
				},
				{
					title: sessionsHelper.localizeSessionType('inPersonMeeting'),
					image: appSetup.sessionCardImages.inPersonMeeting?.[0]?.image,
					click: () => {
						this.createSession({
							sessionType: 'inPersonMeeting',
							stateItems: [],
							workout: undefined
						});

						this.dependencies.dialogService.closeAllDialogs();
					}
				}
			);
		}

		return cardItems;
	});

	public readonly workoutItems: Signal<ListItem<WorkoutDb.IWorkoutItem>[]> = computed(() => {
		const selectionItems: ListItem<WorkoutDb.IWorkoutItem>[] = [];

		for (const container of this.sharedData()?.data?.training?.containers ?? []) {
			for (const item of container.items) {
				selectionItems.push({
					data: item.workout,
					title: `${container.title} - ${item.workout.system.name}`,
					id: item.workout.id,
					avatar: item.workout.avatar?.[0]?.image,
					select: () => {
						this.dependencies.dialogService.closeAllDialogs();
						this.createSession({
							sessionType: 'workout',
							stateItems: sessionsHelper.getStateItems({
								workout: item.workout,
								availableExercises: this.availableExercises() ?? []
							}),
							workout: item.workout
						});
					}
				});
			}
		}

		return selectionItems;
	});

	public readonly enableDelete: Signal<boolean> = computed(() => {
		return this.authUser()?.userType === 'trainer';
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.initSessions(sharedData.data);
			this.initExercises(sharedData.data);
		};
	}

	openCreateSessionDialog(): void {
		this.dependencies.dialogService.openDialog({
			title: 'Create session',
			subtitle:
				this.authUser()?.userType === 'trainer'
					? 'Record your session with client'
					: 'Record your workout session',
			size: 'large',
			template: () => this.newSessionTemplate
		});
	}

	handleSilentUpdate(session: SessionDb.ISessionItem): void {
		this.sessionItems.update((sessionItems) => {
			if (!sessionItems) {
				return [];
			}
			const index = sessionItems.findIndex((m) => m.id === session.id);

			if (index >= 0) {
				sessionItems[index] = session;
			}
			return [...sessionItems];
		});
	}

	handleSessionEdit(session: SessionDb.ISessionItem): void {
		this.handleSilentUpdate(session);
		this.dependencies.dialogService.closeAllDialogs();
	}

	private openEditSessionDialog(): void {
		const session = this.selectedSessionItem();

		if (!session) {
			return;
		}
		let subtitle: string = sessionsHelper.localizeSessionType(session.sessionType);

		if (session.sessionType === 'workout' && session.workout) {
			subtitle = `${sessionsHelper.localizeSessionType(session.sessionType)} - ${session.workout.system.name}`;
		}

		this.dependencies.dialogService.openDialog({
			title: 'Edit session',
			subtitle: subtitle,
			size: 'large',
			template: () => this.editSessionTemplate
		});
	}

	private openViewSessionDialog(): void {
		const session = this.selectedSessionItem();

		if (!session) {
			return;
		}

		this.dependencies.dialogService.openTextDialog({
			title: session.system.name,
			subtitle: super.formatDateTime(session.system.created),
			text: session.notes ?? 'This session has no notes'
		});
	}

	private openSelectWorkoutDialog(): void {
		this.dependencies.dialogService.openDialog({
			title: 'Select workout',
			subtitle:
				this.authUser()?.userType === 'trainer'
					? 'You may choose only from the workouts assigned to client'
					: 'Choose workout you want to record',
			size: 'large',
			template: () => this.selectWorkoutTemplate
		});
	}

	private createSession(data: {
		sessionType: SessionDb.SessionType;
		stateItems: readonly SessionDb.ISessionStateItem[];
		workout: WorkoutDb.IWorkoutItem | undefined;
	}): void {
		const sharedData = this.sharedData();

		if (!sharedData?.data) {
			return;
		}

		super.subscribeToObservable(
			this.dependencies.httpService
				.create<SessionDb.ISessionItem, SessionDb.IBaseSessionItem>({
					type: 'session',
					createModel: {
						name: `Session`,
						notes: undefined,
						userId: sharedData.data.id,
						sessionType: data.sessionType,
						date: this.dependencies.timeService.toISODate(this.dependencies.timeService.now()),
						state: data.stateItems,
						workout: data.workout
					}
				})
				.pipe(
					map((response) => {
						super.notifyCreated('Session');
						this.sessionItems.update((sessionItems) => {
							return [...(sessionItems ?? []), response.data];
						});

						this.selectedSessionItem.set(response.data);
						this.openEditSessionDialog();
					})
				)
		);
	}

	private handleSessionClick(session: SessionDb.ISessionItem): void {
		this.selectedSessionItem.set(session);
		this.openEditSessionDialog();
	}

	private getTable(sessionItems: SessionDb.ISessionItem[]): ITable<SessionDb.ISessionItem> {
		const table: ITable<SessionDb.ISessionItem> = {
			itemType: 'Session',
			noItemsText: {
				noItems: this.authUserIsClient() ? 'You have no recorded sessions' : `Client has no recorded sessions`,
				noFilteredItems: `No sessions match your filter`
			},
			search: {
				type: 'local'
			},
			tableButtons: [
				{
					icon: 'add',
					buttonType: 'primary',
					buttonSize: 'default',
					click: () => {
						this.openCreateSessionDialog();
					},
					title: `New session`
				}
			],
			deleteItems: this.enableDelete()
				? (items) => {
						return this.dependencies.httpService
							.deleteMultiple<SessionDb.ISessionItem>(
								'session',
								items.map((m) => m.id)
							)
							.pipe(
								map((response) => {
									return items;
								})
							);
				  }
				: undefined,
			items: sessionItems,
			id: (item) => item.id,
			title: {
				title: 'Title',
				value: (item) => item.system.name
			},
			subtitle: (item) => {
				if (item.workout) {
					return `${sessionsHelper.localizeSessionType(item.sessionType)} - ${item.workout.system.name}`;
				}
				return sessionsHelper.localizeSessionType(item.sessionType);
			},
			mainItemClick: (item) => {
				this.handleSessionClick(item);
			},
			fields: [
				{
					format: 'date',
					title: `Date`,
					value: (item) => this.dependencies.timeService.toDate(item.date),
					sortBy: (item) => new Date(item.date),
					isDefaultSort: true
				},
				{
					format: 'tag',
					title: `Completion`,
					tagType: (item) => {
						return sessionsHelper.getComplianceTagType(sessionsHelper.calculateComplianceScore(item));
					},
					value: (item) => {
						if (item.state.length) {
							return `${sessionsHelper.calculateComplianceScore(item)}%`;
						}
						return undefined;
					},
					sortBy: (item) => sessionsHelper.calculateComplianceScore(item),
					isDefaultSort: true
				}
			]
		};

		return table;
	}

	private initSessions(user: UserDb.IUserItem): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<SessionDb.ISessionItem[]>((builder) =>
					builder.withCustomApiRoute('session', 'getSessionsForUser').withId(user.id).build()
				)
				.pipe(
					map((response) => {
						this.sessionItems.set(response.data);
					})
				)
		);
	}

	private initExercises(user: UserDb.IUserItem): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<ExerciseDb.IExerciseItem[]>((builder) =>
					builder.withCustomApiRoute('exercise', 'exercisesForWorkout').build()
				)
				.pipe(
					map((response) => {
						this.availableExercises.set(response.data);
					})
				)
		);
	}
}
